'use strict';

var framerMotion = require('framer-motion');
var React = require('react');
var reactIcons = require('@sravni/react-icons');
var reactUtils = require('@sravni/react-utils');
var useBackwardEvent = require('../../../hooks/useBackwardEvent.js');
var Icon = require('../../Icon/src/Icon.js');
var Portal = require('../../Portal/src/Portal.js');
var _utils = require('../../Transition/src/_utils.js');
require('../../Transition/src/Fade.js');
var ScaleFade = require('../../Transition/src/ScaleFade.js');
require('../../Transition/src/SlideFade.js');
require('../../Transition/src/Collapse.js');
var Dialog_module = require('../styles/Dialog.module.less.js');
var DialogContext = require('./DialogContext.js');

function _interopNamespaceDefault(e) {
    var n = Object.create(null);
    if (e) {
        Object.keys(e).forEach(function (k) {
            if (k !== 'default') {
                var d = Object.getOwnPropertyDescriptor(e, k);
                Object.defineProperty(n, k, d.get ? d : {
                    enumerable: true,
                    get: function () { return e[k]; }
                });
            }
        });
    }
    n.default = e;
    return Object.freeze(n);
}

var React__namespace = /*#__PURE__*/_interopNamespaceDefault(React);

/**
 * @description The Dialog component is used to show content on top of an overlay that requires user interaction
 *
 * @component
 * @example
 * ```jsx
 * <Dialog title="Title">content</Dialog>
 * ```
 */
const Dialog = React__namespace.forwardRef((props, ref) => {
    const { visible, fullscreen, closable, closeOnBackward = false, className, children, onClose = reactUtils.noop, ...restProps } = props;
    const cx = reactUtils.useStyles(Dialog_module.default);
    const isUnmountEvent = React__namespace.useRef(true);
    // 'isActive' used to fix the problem of simultaneously exiting with Esc in nested dialogs
    const [isActive, setIsActive] = React__namespace.useState(true);
    const { setIsActivePreviousDialog } = React__namespace.useContext(DialogContext.default);
    const animationProps = React__namespace.useMemo(() => ({ initialScale: fullscreen ? 1.05 : 0.95, reverse: true }), [fullscreen]);
    const dialogMotionProps = React__namespace.useMemo(() => ({
        ...ScaleFade.scaleFadeConfig,
        custom: animationProps,
        animate: visible ? 'enter' : 'exit',
    }), [animationProps, visible]);
    const backdropMotionProps = React__namespace.useMemo(() => ({
        variants: {
            exit: {
                opacity: 0,
                transition: {
                    duration: 0.15,
                    ease: _utils.EASINGS.easeOut,
                },
            },
            enter: {
                opacity: 0.6,
                transition: {
                    duration: 0.2,
                    ease: _utils.EASINGS.easeIn,
                },
            },
        },
        animate: visible ? 'enter' : 'exit',
        initial: 'exit',
        exit: 'exit',
    }), [visible]);
    const dialogContextProviderValue = React__namespace.useMemo(() => ({ fullscreen, setIsActivePreviousDialog: setIsActive }), [fullscreen]);
    const handleClose = React__namespace.useCallback((event) => {
        reactUtils.preventBodyScroll();
        setIsActivePreviousDialog?.(true);
        onClose?.(event);
        isUnmountEvent.current = false;
    }, [onClose, setIsActivePreviousDialog]);
    const handlePreventBodyScroll = React__namespace.useCallback(() => {
        if (visible && isUnmountEvent.current) {
            reactUtils.preventBodyScroll();
        }
    }, [visible, isUnmountEvent]);
    React__namespace.useEffect(() => {
        if (visible) {
            reactUtils.preventBodyScroll(true);
            setIsActivePreviousDialog?.(false);
            isUnmountEvent.current = true;
        }
        else {
            setIsActivePreviousDialog?.(true);
        }
        return handlePreventBodyScroll;
    }, [handlePreventBodyScroll, setIsActivePreviousDialog, visible]);
    reactUtils.useKeyPress({
        enabled: !!visible,
        keyMap: {
            Escape: (event) => {
                event.preventDefault();
                if (!isActive) {
                    return;
                }
                setIsActivePreviousDialog?.(true);
                handleClose(event);
            },
        },
    }, [visible, isActive]);
    const { closeEventHandler } = useBackwardEvent.useBackwardEvent(closeOnBackward, !!visible, handleClose);
    return (React__namespace.createElement(Portal.Portal, null,
        React__namespace.createElement(framerMotion.AnimatePresence, { custom: animationProps, onExitComplete: closeEventHandler }, visible && (React__namespace.createElement("div", { className: cx('overlay') },
            !fullscreen && React__namespace.createElement(framerMotion.motion.div, { ...backdropMotionProps, className: cx('backdrop'), onClick: handleClose }),
            React__namespace.createElement(framerMotion.motion.div, { ...dialogMotionProps, ref: ref, "data-qa": "Dialog", className: cx(className, 'container', { fullscreen }), ...restProps },
                closable && React__namespace.createElement(Icon.Icon, { className: cx('close'), size: 28, icon: React__namespace.createElement(reactIcons.Cross, null), onClick: handleClose }),
                React__namespace.createElement(DialogContext.default.Provider, { value: dialogContextProviderValue }, children)))))));
});
Dialog.defaultProps = {
    closable: true,
    closeOnBackward: false,
};
Dialog.displayName = 'Dialog';

exports.Dialog = Dialog;
//# sourceMappingURL=Dialog.js.map
